import { Location } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { API_BASE } from '../api-base.token';
import { CreateTrainingRequest } from './create-training-request';
import { EmergencyContactInformationRequest } from './emergency-contact-information-request';
import { EmergencyContactInformationResponse } from './emergency-contact-information-response';
import { SignInRequest } from './sign-in-request';
import { GetVisitorSignInInformationQuery } from './sign-in-response';
import { SignOutRequest } from './sign-out-request';
import { TrainingEventResponse } from './training-event-response';
import { TrainingMaterialRequest } from './training-material-request';
import { TrainingMaterialResponse } from './training-material-response';
import { GetVisitorRequest, VisitorRequest } from './visitor-request';
import { VisitorResponse } from './visitor-response';

const path = 'visitor-sign-ins';

export class GetVisitorSignInsOptions {
  pageNumber = 10;
  pageSize = 10;
  onlyReturnOnsiteVisitors? = false;
}

@Injectable({
  providedIn: 'root',
})
export class VisitorSignInsHttpService {
  constructor(
    private readonly httpClient: HttpClient,
    @Inject(API_BASE)
    private readonly apiBase: string
  ) {}

  get(options: GetVisitorSignInsOptions): Observable<GetVisitorSignInInformationQuery> {
    const url = Location.joinWithSlash(this.apiBase, `${path}/v2`);

    const params = new HttpParams()
      .set('pageNumber', options.pageNumber.toString())
      .set('pageSize', options.pageSize.toString())
      .set(
        'onlyReturnOnsiteVisitors',
        (options?.onlyReturnOnsiteVisitors ?? false).toString()
      );

    return this.httpClient.get<GetVisitorSignInInformationQuery>(url, { params });
  }

  signIn(request: SignInRequest) {
    const url = Location.joinWithSlash(this.apiBase, path);

    return this.httpClient.post(url, request);
  }

  signOut(request: SignOutRequest) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/sign-out`);

    return this.httpClient.post(url, request);
  }

  createTrainingMaterial(request: TrainingMaterialRequest) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/training-material`
    );

    return this.httpClient.post(url, request);
  }

  getTrainingMaterial() {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/v2/training-material`
    );

    return this.httpClient.get<TrainingMaterialResponse[]>(url);
  }

  getVisitor(request: GetVisitorRequest) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/get-visitor`);

    return this.httpClient.post<VisitorResponse | null>(url, request);
  }

  createVisitor(request: VisitorRequest) {
    const url = Location.joinWithSlash(this.apiBase, `${path}/visitor`);

    return this.httpClient.post<VisitorResponse | null>(url, request);
  }

  createTraining(request: CreateTrainingRequest) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/visitor-training`
    );

    return this.httpClient.post(url, request);
  }

  getTrainingEvent(visitorId: string) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/${visitorId}/training`
    );

    return this.httpClient.get<TrainingEventResponse | null>(url);
  }

  getEmergencyContactInformation() {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/emergency-contact-information`
    );

    return this.httpClient.get<EmergencyContactInformationResponse[]>(url);
  }

  createEmergencyContactInformation(
    request: EmergencyContactInformationRequest
  ) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/emergency-contact-information`
    );
    return this.httpClient.post(url, request);
  }

  deleteEmergencyContactInformation(id: number) {
    const url = Location.joinWithSlash(
      this.apiBase,
      `${path}/emergency-contact-information/${id}`
    );
    return this.httpClient.delete(url);
  }
}
